import { useTranslation } from 'react-i18next';

import { cn } from '@/design-system';

import { StatusGlyph } from './glyph';
import { registries, type RegistryName, type RegistryValue, type StatusGroup } from './registries';

/**
 * Group presentation (CLAUDE.md §14). Text tones carry meaning together with the glyph shape, never alone.
 * Attention: the row's own surface + 3px amber inline-start rule + amber glyph/text — no cream background.
 * Final (solid green) is reserved for Disbursed, the strongest completed state.
 */
const statusGroupClass: Record<StatusGroup, { box: string; glyph: string; text: string }> = {
  neutral: { box: '', glyph: 'text-ink-muted', text: 'text-ink' },
  progress: { box: '', glyph: 'text-info', text: 'font-medium text-info' },
  attention: { box: 'edge-start edge-warning rounded-none ps-2', glyph: 'text-warning-edge', text: 'font-medium text-warning' },
  positive: { box: '', glyph: 'text-positive', text: 'font-medium text-positive' },
  negative: { box: '', glyph: 'text-negative', text: 'font-medium text-negative' },
  final: { box: 'bg-positive px-1.5', glyph: 'text-primary-foreground', text: 'font-medium text-primary-foreground' },
  'final-muted': { box: '', glyph: 'text-ink-muted', text: 'text-ink-muted' },
};

export interface StatusBadgeProps<R extends RegistryName> {
  registry: R;
  value: RegistryValue<R>;
  /** Secondary grey text, e.g. the bank's own sub-status. Never a second badge. */
  detail?: string | null;
  /** Interpolation values for labels that name something, e.g. { bank: 'SIB' } for "Not on {{bank}}'s list". */
  params?: Record<string, string>;
  className?: string;
}

/** Status: glyph + word in the group tone. Attention carries an amber inline-start rule; only Disbursed is solid. */
export function StatusBadge<R extends RegistryName>({ registry, value, detail, params, className }: StatusBadgeProps<R>) {
  const { t } = useTranslation('status');
  const reg = registries[registry] as Record<string, { group: StatusGroup; glyph: Parameters<typeof StatusGlyph>[0]['glyph']; labelKey: string }>;
  const s = reg[value];
  if (!s) return <span className="text-ink-muted">{value}</span>;
  const c = statusGroupClass[s.group];
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2 whitespace-nowrap', className)}>
      <span data-status-group={s.group} className={cn('inline-flex h-5.5 items-center gap-1.5 rounded-sm text-table leading-none', c.box)}>
        <StatusGlyph glyph={s.glyph} className={c.glyph} />
        <span className={c.text}>{params ? t(s.labelKey, params) : t(s.labelKey)}</span>
      </span>
      {detail ? <span className="truncate text-meta text-ink-muted">{detail}</span> : null}
    </span>
  );
}

export function DealStatus({ stage, subStatus, className }: { stage: RegistryValue<'dealStage'>; subStatus?: string | null; className?: string }) {
  return <StatusBadge registry="dealStage" value={stage} detail={subStatus} className={className} />;
}

/** Label only (for selects, filters, stepper). */
export function useStatusLabel() {
  const { t } = useTranslation('status');
  return <R extends RegistryName>(registry: R, value: RegistryValue<R>) => {
    const reg = registries[registry] as Record<string, { labelKey: string }>;
    return reg[value] ? t(reg[value].labelKey) : value;
  };
}
